import { useState } from 'react';
import { Box, Group, SimpleGrid, Stack, Switch, Text } from '@mantine/core';
import type { CSSProperties, ReactNode } from 'react';
import { TV_PREVIEW_GEOMETRY as G, TV_TRUE_SIZE_NOTE } from '@/lib/constants/appFactory';

interface TvDevicePreviewsProps {
  /** name → URL of the set's files (BrandKitUtils.previewFileMap). */
  files: Record<string, string>;
  /** Width of the scaled full-screen frame (the splash), px (the frame is 16:9). */
  frameWidth?: number;
  /**
   * Which platform's screens to draw: the set serves Fire TV and Google TV,
   * and an app with only one of them on never sees the other's screens.
   * Absent = both.
   */
  show?: { firetv?: boolean; googletv?: boolean };
}

const FRAME_BG = '#0b0e14';
const FRAME_BORDER = '6px solid #1c1f26';
const PLACEHOLDER = '#2a2f3a';

/**
 * The Android-family set drawn where each TV shows it (TV graphics v2, design
 * D5) and, since the app-level Brand Kit (3.42.0), at the TRUE on-screen size:
 * one CSS pixel per pixel of a 1080p screen for the Google TV circle (160 px),
 * the Google TV Apps-page banner (320 x 180) and the Fire TV home tile
 * (approximate: Amazon publishes the 1280 x 720 art and its 882 x 448
 * content-safe area, not the tile's size) — a logo is judged at the size a
 * viewer gets, with neutral neighbours beside it. A strip wider than the page
 * scrolls sideways; ours is first so it is always in view. The splash is a
 * full screen, so it stays a scaled frame. Pure CSS: the adaptive icon is the
 * two 108 dp layers with the central 72 dp showing through a circle, as the
 * launcher masks it.
 */
export function TvDevicePreviews({ files, frameWidth = 560, show }: TvDevicePreviewsProps) {
  const [safeArea, setSafeArea] = useState(false);
  const [focused, setFocused] = useState(true);
  const googletv = show?.googletv ?? true;
  const firetv = show?.firetv ?? true;

  return (
    <Stack gap="lg">
      {googletv && (
        <Preview title="Google TV — Your apps" note="The home screen shows your app as a circle.">
          <TrueSizeStrip gap={28}>
            <AdaptiveCircle files={files} size={G.googleTvCirclePx} />
            <Circle size={G.googleTvCirclePx} />
            <Circle size={G.googleTvCirclePx} />
            <Circle size={G.googleTvCirclePx} />
          </TrueSizeStrip>
        </Preview>
      )}

      {googletv && (
        <Preview title="Google TV — Apps page" note="The wide banner in the list of installed apps.">
          <TrueSizeStrip gap={24}>
            <Tile w={G.googleTvBannerPx.w} h={G.googleTvBannerPx.h} src={files['tv-banner.png']} alt="TV banner" />
            <Tile w={G.googleTvBannerPx.w} h={G.googleTvBannerPx.h} />
            <Tile w={G.googleTvBannerPx.w} h={G.googleTvBannerPx.h} />
          </TrueSizeStrip>
        </Preview>
      )}

      {firetv && (
        <Preview title="Fire TV — Home row" note="This image comes from your Amazon listing (the Store Icon).">
          <TrueSizeStrip gap={24}>
            <Tile
              w={G.fireTvTilePx.w}
              h={G.fireTvTilePx.h}
              src={files['listing-icon.png']}
              alt="Fire TV home screen icon"
              safeArea={safeArea}
              focused={focused}
            />
            <Tile w={G.fireTvTilePx.w} h={G.fireTvTilePx.h} />
            <Tile w={G.fireTvTilePx.w} h={G.fireTvTilePx.h} />
          </TrueSizeStrip>
          <Group gap="md" mt={6}>
            <Switch size="xs" label="Show the safe area" checked={safeArea} onChange={(e) => setSafeArea(e.currentTarget.checked)} />
            <Switch size="xs" label="Focused" checked={focused} onChange={(e) => setFocused(e.currentTarget.checked)} />
          </Group>
        </Preview>
      )}

      {googletv && (
        <Preview title="Google Play listing" note="The icon, the TV banner and the feature graphic of the store page.">
          <SimpleGrid cols={3} spacing="sm" maw={720}>
            <Img src={files['icon.png']} alt="Store icon" style={{ aspectRatio: '1 / 1', borderRadius: '20%' }} />
            <Img src={files['listing-icon.png']} alt="TV banner" style={{ aspectRatio: '16 / 9' }} />
            <Img src={files['listing-feature-graphic.png']} alt="Feature graphic" style={{ aspectRatio: '1024 / 500' }} />
          </SimpleGrid>
        </Preview>
      )}

      <Preview title="Splash" note="What a viewer sees while the app opens (the whole screen, scaled down).">
        <TvFrame width={frameWidth} ground={files['icon-background.png']}>
          {files['splash-logo.png'] ? (
            <img
              src={files['splash-logo.png']}
              alt="Splash logo"
              style={{ maxHeight: '40%', maxWidth: '50%', objectFit: 'contain' }}
            />
          ) : (
            <Missing />
          )}
        </TvFrame>
      </Preview>
    </Stack>
  );
}

// Shared with RokuScreenPreviews: a titled preview, the true-size strip, a 16:9 TV frame, the missing-file mark.
export function Preview({ title, note, children }: { title: string; note: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="sm" fw={500}>
        {title}
      </Text>
      <Text size="xs" c="dimmed">
        {note}
      </Text>
      {children}
    </Stack>
  );
}

/**
 * A piece of a 1080p TV screen at one CSS pixel per screen pixel: the items
 * keep their real size whatever the page width, and the strip scrolls
 * sideways when it does not fit. The padding leaves room for a focused tile's
 * enlargement.
 */
export function TrueSizeStrip({ gap, children }: { gap: number; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Box
        data-testid="tv-true-size"
        style={{ maxWidth: '100%', width: 'fit-content', overflowX: 'auto', background: FRAME_BG, borderRadius: 8, border: FRAME_BORDER }}
      >
        <Box style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap, padding: 28, width: 'max-content' }}>
          {children}
        </Box>
      </Box>
      <Text size="xs" c="dimmed">
        {TV_TRUE_SIZE_NOTE}
      </Text>
    </Stack>
  );
}

export function TvFrame({ width, ground, children }: { width: number; ground?: string; children: ReactNode }) {
  return (
    <Box
      data-testid="tv-frame"
      style={{
        width,
        maxWidth: '100%',
        aspectRatio: '16 / 9',
        background: ground ? `url(${ground}) center / cover no-repeat` : FRAME_BG,
        borderRadius: 8,
        border: FRAME_BORDER,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
}

function Circle({ size }: { size: number }) {
  return <Box style={{ width: size, height: size, borderRadius: '50%', background: PLACEHOLDER, flex: 'none' }} />;
}

/**
 * The adaptive icon as the launcher draws it: both 108 dp layers at
 * size / (72/108), centred, clipped to a circle of `size`. Without the two
 * layers (a set from before composer v2) the old square icon stands in.
 */
function AdaptiveCircle({ files, size }: { files: Record<string, string>; size: number }) {
  const fg = files['icon-foreground.png'];
  const bg = files['icon-background.png'];
  const layer = size / G.adaptiveVisibleFrac;
  const off = (size - layer) / 2;
  const layerStyle: CSSProperties = { position: 'absolute', left: off, top: off, width: layer, height: layer };
  return (
    <Box
      data-testid="google-tv-circle"
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        flex: 'none',
        // a set with no icon at all (a partial upload) is a placeholder that says so, not a white app icon
        background: fg || files['icon.png'] ? '#ffffff' : PLACEHOLDER,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {fg ? (
        <>
          {bg && <img src={bg} alt="" style={layerStyle} />}
          <img src={fg} alt="Google TV icon" style={layerStyle} />
        </>
      ) : files['icon.png'] ? (
        <img src={files['icon.png']} alt="Google TV icon (from the app icon)" style={{ ...layerStyle, left: size * 0.17, top: size * 0.17, width: size * 0.66, height: size * 0.66 }} />
      ) : (
        <Missing />
      )}
    </Box>
  );
}

function Tile({ w, h, src, alt, safeArea, focused }: { w: number; h: number; src?: string; alt?: string; safeArea?: boolean; focused?: boolean }) {
  const sa = G.fireTvSafeArea;
  return (
    <Box
      style={{
        position: 'relative',
        width: w,
        height: h,
        flex: 'none',
        borderRadius: 4,
        overflow: 'hidden',
        background: PLACEHOLDER,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transform: focused ? 'scale(1.1)' : undefined,
        boxShadow: focused ? '0 0 0 2px #ffffff' : undefined,
      }}
    >
      {src ? <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : alt ? <Missing /> : null}
      {safeArea && (
        <Box
          data-testid="fire-tv-safe-area"
          style={{
            position: 'absolute',
            left: `${((1 - sa.w / sa.of.w) / 2) * 100}%`,
            top: `${((1 - sa.h / sa.of.h) / 2) * 100}%`,
            width: `${(sa.w / sa.of.w) * 100}%`,
            height: `${(sa.h / sa.of.h) * 100}%`,
            border: '1px dashed #ffd400',
          }}
        />
      )}
    </Box>
  );
}

function Img({ src, alt, style }: { src?: string; alt: string; style: CSSProperties }) {
  return src ? (
    <img src={src} alt={alt} style={{ width: '100%', objectFit: 'cover', background: PLACEHOLDER, ...style }} />
  ) : (
    <Box style={{ ...style, width: '100%', background: PLACEHOLDER, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Missing />
    </Box>
  );
}

export function Missing() {
  return (
    <Text size="xs" c="dimmed">
      not in this set
    </Text>
  );
}
